import React, { useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

import NumberColored from '#/components/basic/text/NumberColored';
import { formatNumber } from '#/lib/display/formatting';

import { ROW_HEIGHT } from './orderBookRowUtils';
import { LevelTwoOrderBookRow } from './orderBookServiceUtils';

import type { HoveredRow } from './types';

const UPDATE_TRANSITION_SPEED = 0.5;

const appearAnimationBid = keyframes`
  0% {
    /* opacity: 0.2; */
  }
  20% {
    background-color: var(--green1b);
  }
  100% {
    /* opacity: 1; */
  }
`;

const appearAnimationAsk = keyframes`
  0% {
    /* opacity: 0.2; */
  }
  20% {
      background-color: var(--red1b);
  }
  100% {
    /* opacity: 1; */
  }
`;

type RowParamsType = {
  $isBid: boolean;
  $isHighlighted: boolean;
  $isHovered: boolean;
  $isUserOrderHighlight: boolean;
};

const Row = styled.div<RowParamsType>`
  height: ${ROW_HEIGHT}px;
  display: flex;
  align-items: stretch;

  cursor: pointer;

  /* transition: opacity 300ms ease-in, background-color 400ms ease-in; */
  transition: background-color 400ms ease;
  animation: ${props =>
      props.$isBid ? appearAnimationBid : appearAnimationAsk}
    0.8s;

  /* transition: 0.4s background-color ease; */

  background: ${props =>
    props.$isHighlighted
      ? props.$isBid
        ? 'var(--green1b)' //'var(--green2)'
        : 'var(--red1b)' // 'var(--red2)'
      : props.$isUserOrderHighlight
      ? props.$isBid
        ? 'var(--black1)'
        : 'var(--black1)'
      : 'initial'};

  ${({ $isHovered }) => $isHovered && 'background: var(--black1);'}
  &:active,
  &:focus {
    background: var(--gray0);
  }

  ${props =>
    props.$isUserOrderHighlight
      ? 'border-left: 2px solid var(--primary5);'
      : ''}
`;

const Column = styled.div<{ $minimal: boolean }>`
  flex-grow: 0;
  flex-shrink: 0;
  width: 0;
  width: 100px;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  font-family: var(--digitsFontFamily);
`;

const PriceQuantityWrapColumn = styled(Column)`
  position: relative;
  flex: ${({ $minimal }) => ($minimal ? 1 : 5.5)};
  display: flex;
`;

const PriceColumn = styled(Column)`
  flex-basis: 120px;
  text-align: left;
  padding: 7px 5px 7px 12px;
  z-index: 123;
`;

const QuantityColumn = styled(Column)`
  flex: 4;
  text-align: right;
  padding: 7px 5px;
  z-index: 123;
`;

const TotalColumn = styled(Column)`
  flex: ${({ $minimal }) => ($minimal ? 1 : 2.1)};
  position: relative;
`;

const TotalText = styled.div`
  text-align: right;
  z-index: 123;
  position: absolute;
  top: 6px;
  right: 5px;
  color: var(--white);
`;

const ProgressBar = styled.div<{
  $isHighlighted: boolean;
  $isHovered: boolean;
}>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  transition: 0.5s width ease;
  background: ${props =>
    props.$isHighlighted ? 'rgba(0,0,0,0)' : 'var(--gray1)'};
  opacity: 0.7;
  ${({ $isHovered }) => $isHovered && 'opacity: 0.5;'}
`;

const AmountProgressbar = styled.div<{ $color: string; $isHovered: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  transition: 0.5s width ease;
  background: ${props => props.$color};

  ${({ $isHovered }) => $isHovered && 'filter: brightness(150%);'}
`;

const HoverSummaryArrow = styled.div`
  position: absolute;
  top: 3px;
  z-index: 999999;
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-left: 10px solid var(--gray2a);
`;

interface Props {
  depthPercentage?: string;
  quantity: string;
  priceFormatted: string;
  total: number;
  totalPercentage?: number;
  orderType: 'ask' | 'bid';
  onClick?: () => void;
  userOrderHighlight: boolean;
  isHovered: boolean;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: () => void;
  summaryTooltipEnabled?: boolean;
  minimalLayout: boolean;
}

class OrderBookRow extends React.Component<Props> {
  highlightTimeout?: number;

  state = {
    highlighted: false,
  };

  componentDidUpdate(prevProps: Props) {
    if (prevProps.quantity !== this.props.quantity) {
      this.setState({ highlighted: true }, () => {
        this.highlightTimeout = window.setTimeout(
          () => {
            this.setState({ highlighted: false });
          },
          (UPDATE_TRANSITION_SPEED / 2) * 1000
        );
      });
    }
  }

  componentWillUnmount() {
    if (this.highlightTimeout) {
      clearTimeout(this.highlightTimeout);
    }
  }

  render() {
    const { highlighted } = this.state;
    const {
      depthPercentage,
      orderType,
      priceFormatted,
      total,
      quantity,
      totalPercentage,
      onClick,
      userOrderHighlight,
      summaryTooltipEnabled,
      isHovered,
      minimalLayout,
    } = this.props;

    const columns = (
      <>
        <PriceQuantityWrapColumn $minimal={minimalLayout}>
          {summaryTooltipEnabled && isHovered && <HoverSummaryArrow />}
          <PriceColumn $minimal={minimalLayout}>
            <NumberColored
              color={orderType === 'bid' ? 'green' : 'red'}
              value={priceFormatted}
            />
          </PriceColumn>
          {!minimalLayout && (
            <QuantityColumn $minimal={false}>
              <div style={{ zIndex: 2, position: 'relative' }}>
                <NumberColored
                  color={orderType === 'bid' ? 'green' : 'red'}
                  value={quantity}
                />
              </div>
            </QuantityColumn>
          )}
          <AmountProgressbar
            style={{
              width: `${
                depthPercentage ? Math.min(Number(depthPercentage), 100) : 0
              }%`,
            }}
            $color={
              highlighted
                ? 'rgba(0,0,0,0)'
                : orderType === 'bid'
                ? 'var(--green3b)'
                : 'var(--red2d)'
            }
            $isHovered={isHovered}
          />
        </PriceQuantityWrapColumn>
        <TotalColumn $minimal={minimalLayout}>
          <TotalText>{formatNumber(total, { fixedDecimals: 2 })}</TotalText>
          <ProgressBar
            style={{ width: `${totalPercentage || 0}%` }}
            $isHighlighted={highlighted}
            $isHovered={isHovered}
          />
        </TotalColumn>
      </>
    );

    return (
      <Row
        $isHighlighted={highlighted}
        $isHovered={isHovered}
        $isUserOrderHighlight={userOrderHighlight}
        onClick={onClick}
        $isBid={orderType === 'bid'}
        onMouseEnter={this.props.onMouseEnter}
        onMouseLeave={this.props.onMouseLeave}
      >
        {columns}
      </Row>
    );
  }
}

function OrderbookRowWrapper({
  type,
  order,
  index,
  onClick,
  onMouseLeave,
  totalPercentage,
  depthPercentage,
  isHovered,
  onSetHoveredRow,
  userOrdersPriceLevels,
  summaryTooltipEnabled,
  minimalLayout,
}: {
  type: 'ask' | 'bid';
  order: LevelTwoOrderBookRow;
  index: number;
  isHovered: boolean;
  onMouseLeave: () => void;
  totalPercentage: number | undefined;
  depthPercentage: string | undefined;
  onSetHoveredRow: (row: HoveredRow) => void;
  userOrdersPriceLevels: string[];
  onClick: (index: number) => void;
  summaryTooltipEnabled: boolean;
  minimalLayout: boolean;
}) {
  const handleMouseEnter = useCallback<React.MouseEventHandler<HTMLDivElement>>(
    event =>
      onSetHoveredRow({
        type,
        index,
        yPosition: event.currentTarget.getBoundingClientRect().y,
      }),
    [index, type, onSetHoveredRow]
  );
  const handleClick = useCallback(() => onClick(index), [index, onClick]);
  return (
    <OrderBookRow
      priceFormatted={order.priceFormatted}
      quantity={order.quantity}
      onClick={handleClick}
      userOrderHighlight={userOrdersPriceLevels.includes(order.price)}
      isHovered={isHovered}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onMouseLeave}
      total={order.total}
      totalPercentage={totalPercentage}
      depthPercentage={depthPercentage}
      orderType={type}
      summaryTooltipEnabled={summaryTooltipEnabled}
      minimalLayout={minimalLayout}
    />
  );
}

// Just in case of any issues this can be hotfix (second parameter of `memo`),
// but we should always try to fix reason first
// function rowPropsAreEqual(prev: any, next: any) {
//   return (
//     prev.index === next.index &&
//     prev.order === next.order &&
//     prev.isHovered === next.isHovered
//   );
// }

const MemoizedOrderbookRow = React.memo(OrderbookRowWrapper);

export default MemoizedOrderbookRow;
